// Shape of CustomDashboard.content — the free-form JSON that stores a BI
// dashboard as FROZEN RESULTS (no EQL at view time). See apiCustomDashboard.js.
//
//   content = {
//     version, layout: "one_page" | "slides",
//     slides: [ { id, title, items: [ Piece ] } ]
//   }
//   Piece = {
//     id, kind: "chart" | "table" | "indicator", title,
//     layout: { x, y, w, h },                 // react-grid-layout cell (12 cols)
//     result: <frozen snapshot>,              // what RenderPiece paints
//     sourceRef: { state, capturedAt, reportsTotal }  // Builder view to re-materialize
//   }
//
// one_page keeps a single implicit slide; slides keeps N. Same schema either
// way, so switching `layout` never reshapes the data.

export const CONTENT_VERSION = 1;

export const PIECE_KINDS = ["chart", "table", "indicator"];

// Slides are fixed-height "pages" (one slide = one PDF page), so the grid is
// capped to this many rows. one_page dashboards are unbounded (scrollable).
export const SLIDE_MAX_ROWS = 16;

export const DEFAULT_LAYOUT = { one_page: "one_page", slides: "slides" };

let seq = 0;
/** Local-only id for slides/pieces (content is per-user, no cross-client keys). */
export const uid = (prefix = "w") =>
  `${prefix}${Date.now().toString(36)}${(seq++).toString(36)}`;

export const emptySlide = (title = "") => ({
  id: uid("s"),
  title,
  items: [],
});

export const emptyContent = (layout = "one_page") => ({
  version: CONTENT_VERSION,
  layout,
  slides: [emptySlide()],
});

/**
 * Tolerates legacy/empty content ({}), a missing slides array, or a bare
 * items list, always returning a well-formed content object for `layout`.
 */
export const normalizeContent = (content, layout = "one_page") => {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return emptyContent(layout);
  }
  const slides = Array.isArray(content.slides) ? content.slides : null;
  if (!slides || !slides.length) {
    // A flat { items: [...] } (or nothing) collapses into a single slide.
    const items = Array.isArray(content.items) ? content.items : [];
    return {
      version: content.version || CONTENT_VERSION,
      layout: content.layout || layout,
      slides: [{ ...emptySlide(), items }],
    };
  }
  return {
    version: content.version || CONTENT_VERSION,
    layout: content.layout || layout,
    slides: slides.map((s) => ({
      id: s.id || uid("s"),
      title: s.title || "",
      items: Array.isArray(s.items) ? s.items : [],
    })),
  };
};

/** Default grid cell for a newly added piece of a given kind (12-col grid). */
export const defaultPieceLayout = (kind, index = 0) => {
  if (kind === "indicator") return { x: (index * 3) % 12, y: Infinity, w: 3, h: 4 };
  if (kind === "table") return { x: 0, y: Infinity, w: 12, h: 10 };
  return { x: (index % 2) * 6, y: Infinity, w: 6, h: 9 }; // chart
};
